<template>
  <div class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingAgencies" class="accordion-header">
        <button
          aria-controls="collapseAgencies"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseAgencies"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="import" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("template.agency.title") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("template.agency.subtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseAgencies"
        aria-labelledby="headingAgencies"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionExample"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <p class="text-sm fw-medium">{{ $t("template.agency.editAgenciesBelongsTo") }}</p>

          <div class="agencies-container">
            <template v-if="!!template.agencies && template.agencies.length > 0">
              <div v-for="agency in template.agencies" :key="agency.id" class="agency d-flex align-items-center">
                <div class="image-container bg-tertiary">
                  {{ agency.name.charAt(0) }}
                </div>

                <div class="agency-data h-100 w-100 d-flex justify-content-between gap-8 align-items-center">
                  <div class="fw-medium">{{ agency.name }}</div>

                  <div class="actions-container text-nowrap" v-if="can('template.detach')">
                    <v-button
                      :disabled="disassociatingAgencyToTemplate"
                      class="p-4"
                      variant="icon"
                      @click="detachAgency(agency)"
                    >
                      <v-icon icon="trash" />
                    </v-button>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="text-sm">{{ $t("template.agency.noAgencies") }}</div>
          </div>

          <template v-if="can('template.attach')">
            <div class="search-separator-container pb-24 mb-4">
              <div class="separator-text-container">
                <span class="separator-text text-sm">{{ $t("template.agency.addAgency") }}</span>
              </div>
            </div>

            <form id="template-agencies-search-form" class="d-flex flex-column justify-content-between h-100">
              <fieldset>
                <legend class="visually-hidden">{{ $t("template.agency.title") }}</legend>

                <v-select-template-agency-search
                  id="template-agencies-search"
                  :selected-options="template.agencies"
                  @selected="assignAgency"
                />
              </fieldset>
            </form>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import VSelectTemplateAgencySearch from "@/components/vendor/VSelectTemplateAgencySearch.vue";
import Agency from "@/api/objects/Agency";
import api from "@/api";
import useCapabilities from "@/helpers/capabilities";
import { computed, getCurrentInstance, ref } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  template: Template;
}>();
const emit = defineEmits(["reloadTemplate"]);
const { can } = useCapabilities();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const { t } = useI18n();

const disableButton = ref<boolean>(false);
const disassociatingAgencyToTemplate = ref(false);

const selectedAgencies = computed(() => {
  return props.template.agencies.map((agency: any) => agency);
});

const assignAgency = async (agency: { id: number; name: string }) => {
  await $modal.confirm({
    title: t("template.agency.addAgencyToTemplateTitle"),
    text: t("template.agency.addAgencyToTemplateTitleText", {
      agency: agency.name,
      template: props.template.name,
    }),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.add"),
    confirmButtonCallback: () => doAssignAgency(agency.id),
  });
};

const doAssignAgency = async (agencyId: number) => {
  try {
    const data = {
      agencies: [agencyId],
    };

    await api.activityService.attachAgencies(props.template.id, data);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  }
};

const detachAgency = async (agency: Agency) => {
  await $modal.delete({
    title: t("template.agency.detachAgencyToTemplateTitle"),
    text: t("template.agency.detachAgencyToTemplateTitleText", {
      agency: agency.name,
      template: props.template.name,
    }),
    deleteButtonText: t("general.button.remove"),
    deleteButtonLoadingText: t("general.button.removing"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doDetachAgency(agency.id),
  });
};

const doDetachAgency = async (agencyId: number) => {
  try {
    disassociatingAgencyToTemplate.value = true;

    const data = {
      agencies: [agencyId],
    };

    await api.activityService.detachAgencies(props.template.id, data);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  } finally {
    disassociatingAgencyToTemplate.value = false;
  }
};
</script>

<style lang="scss">
.agencies-container .agency {
  margin-bottom: 0.5rem;

  .image-container {
    border-bottom-left-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    flex: none;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1.5rem;
    color: var(--uq-light);
  }

  .agency-data {
    padding: 0.563rem 1.25rem 0.563rem 2rem;
    border: 1px solid var(--uq-gray-400);
    border-left: none;
    border-top-right-radius-radius: 0.25rem;
    border-top-right-radius-radius: 0.25rem;
  }
}
</style>
